import type {
  Award,
  Certification,
  CustomSection,
  CustomSectionGroup,
  Education,
  Experience,
  Interest,
  Language,
  Profile,
  Project,
  Publication,
  Reference,
  SectionWithItem,
  Skill,
  Volunteer,
} from "@/lib/schema/sections";
import { TResume } from "@/lib/store/resume";
import { onPreviewSectionClick } from "@/lib/utils/resumeBuilder";
import { isUrl } from "@/utils";
import { isEmptyString } from "@/utils/preview";
import classNames from "classnames";
import { get } from "lodash";
import { Fragment, ReactNode } from "react";
import { TemplateProps } from ".";
import { LevelMap } from "../sidebars/left/sections/Languages";
import { Picture } from "../sidebars/left/sections/shared/Picture";

const Header = ({ resume }: { resume: TResume }) => {
  const basics = resume.data.basics;

  if (!basics) return null;

  return (
    <div
      onClick={() =>
        onPreviewSectionClick(`builder-basics`, {
          highlight: false,
          accordion: false,
        })
      }
      className="flex flex-col items-center justify-between border-b-2 border-black"
    >
      <Picture resume={resume} />
      <div className="mt-2 text-center">
        <div className="mb-4 text-3xl font-bold">{basics.name}</div>
        <div className="text-md flex flex-wrap items-center justify-center space-x-2 font-semibold">
          <div>{basics.headline}</div>
          {basics.location && <div>{basics.location}</div>}
          {basics.phone && (
            <a href={`tel:${basics.phone}`} target="_blank" rel="noreferrer">
              {basics.phone}
            </a>
          )}
          {basics.email && (
            <a href={`mailto:${basics.email}`} target="_blank" rel="noreferrer">
              {basics.email}
            </a>
          )}
          {basics.customFields?.map((item) => (
            <span key={item.id}>
              {[item.name, item.value].filter(Boolean).join(": ")}
            </span>
          ))}
          {basics.website?.href && (
            <a target="_blank" rel="noreferrer" href={basics.website?.href}>
              {basics.website?.href}
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

const Summary = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.summary;
  if (!section?.visible || isEmptyString(section?.content)) return null;

  return (
    <section
      id={section?.id}
      className="section-item flex items-baseline gap-3"
      onClick={() => onPreviewSectionClick(`builder-${section?.id}`)}
    >
      <h4 className="mb-2  w-[140px] flex-shrink-0 py-1 text-base font-bold text-black">
        {section?.name}
      </h4>

      <div
        dangerouslySetInnerHTML={{ __html: section?.content || "" }}
        className="flex-1 "
      />
    </section>
  );
};

type RatingProps = { level: number };

const Rating = ({ level }: RatingProps) => (
  <div className="flex items-center gap-x-1.5">
    {Array.from({ length: 5 }).map((_, index) => (
      <div
        key={index}
        className={classNames(
          "h-2 w-2 rounded-full border border-black",
          level > index && "bg-black",
        )}
      />
    ))}
  </div>
);

type SectionProps<T> = {
  section?: SectionWithItem<T> | CustomSectionGroup;
  children?: (item: T) => React.ReactNode;
  className?: string;
  urlKey?: keyof T;
  levelKey?: keyof T;
  summaryKey?: keyof T;
  keywordsKey?: keyof T;
  leftRender?: (item: T) => ReactNode;
  rightRender?: (item: T) => ReactNode;
};

const Section = <T,>({
  section,
  children,
  className,
  urlKey,
  levelKey,
  summaryKey,
  keywordsKey,
  leftRender,
  rightRender,
}: SectionProps<T>) => {
  if (!section?.visible || !section?.items?.length) return null;

  return (
    <section
      id={section.id}
      className="section-item flex items-baseline gap-6"
      onClick={() =>
        onPreviewSectionClick(`builder-${section?.id}-${section?.items[0]?.id}`)
      }
    >
      <div
        className="grid w-full gap-x-6"
        style={{ gridTemplateColumns: `repeat(1, 1fr)` }}
      >
        {section.items
          .filter((item) => item.visible)
          .map((item, i) => {
            const url = urlKey && get(item, urlKey);
            const level = LevelMap[levelKey && get(item, levelKey, "basic")];

            const summary = (summaryKey && get(item, summaryKey, "")) as
              | string
              | undefined;
            const keywords = (keywordsKey && get(item, keywordsKey, [])) as
              | string[]
              | undefined;
            return (
              <div
                className="flex justify-between gap-3"
                key={item.id}
                onClick={(e) => {
                  e.stopPropagation();
                  onPreviewSectionClick(`builder-${section?.id}-${item.id}`);
                }}
              >
                <div className="w-[140px] text-left">
                  {i === 0 && (
                    <h4 className="text-base font-bold text-black">
                      {section.name}
                    </h4>
                  )}
                  {leftRender?.(item as any)}
                </div>

                <div className="flex-1 space-y-1">
                  {rightRender?.(item as any) && rightRender?.(item as any)}
                  {url?.href && (
                    <a target="_blank" rel="noreferrer" href={url?.href}>
                      {url?.href}
                    </a>
                  )}
                  {level !== undefined && level > 0 && <Rating level={level} />}

                  {keywords !== undefined && keywords.length > 0 && (
                    <p className="text-sm">{keywords.join(", ")}</p>
                  )}

                  {summary !== undefined && !isEmptyString(summary) && (
                    <div dangerouslySetInnerHTML={{ __html: summary }} />
                  )}
                </div>
              </div>
            );
          })}
      </div>
    </section>
  );
};

const Profiles = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.profiles;
  const fontSize = resume.data.settings?.typography.font.size;

  const rightRender = (item: Profile) => {
    return (
      <>
        {isUrl(item.url?.href) ? (
          <a href={item.url?.href} className="text-inherit">
            <img
              className="resume-icon mr-1"
              width={fontSize}
              height={fontSize}
              alt={item.network}
              src={`https://cdn.simpleicons.org/${item.icon}`}
            />
            {item.username}
          </a>
        ) : (
          <div>{item.username}</div>
        )}
        {item.url?.href && (
          <a
            className="block text-sm"
            target="_blank"
            rel="noreferrer"
            href={item.url?.href}
          >
            {item.url?.href}
          </a>
        )}
      </>
    );
  };

  return (
    <Section<Profile> section={section} rightRender={rightRender}></Section>
  );
};

const Experience = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.workExperience;
  const leftRender = (item: Experience) => {
    return (
      <>
        <div>{item.date}</div>
      </>
    );
  };

  const rightRender = (item: Experience) => {
    return (
      <>
        <div className="flex items-center justify-between space-x-1">
          <div className="text-base font-bold">{item.company}</div>
          {item.location && (
            <div className="text-base font-bold">{item.location}</div>
          )}
        </div>
        {item.position && (
          <div className="text-base font-bold italic">{item.position}</div>
        )}
      </>
    );
  };

  return (
    <Section<Experience>
      section={section}
      urlKey="url"
      summaryKey="summary"
      leftRender={leftRender}
      rightRender={rightRender}
    ></Section>
  );
};

const Internship = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.internship;

  const leftRender = (item: Experience) => {
    return (
      <>
        <div>{item.date}</div>
      </>
    );
  };

  const rightRender = (item: Experience) => {
    return (
      <>
        <div className="flex items-center justify-between space-x-1">
          <div className="text-base font-bold">{item.company}</div>
          {item.location && (
            <div className="text-base font-bold">{item.location}</div>
          )}
        </div>
        {item.position && (
          <div className="text-base font-bold italic">{item.position}</div>
        )}
      </>
    );
  };

  return (
    <Section<Experience>
      section={section}
      urlKey="url"
      summaryKey="summary"
      leftRender={leftRender}
      rightRender={rightRender}
    ></Section>
  );
};

const Education = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.education;

  const leftRender = (item: Education) => {
    return (
      <>
        <div>{item.date}</div>
      </>
    );
  };

  const rightRender = (item: Education) => {
    return (
      <>
        <div className="flex items-center justify-between">
          <div className="text-base font-bold">{item.institution}</div>
          <div className="text-base font-bold">{item.studyType}</div>
        </div>
        {item.major && <div>{item.major}</div>}
        {item.score && <div>{item.score}</div>}
        {item.officialSite && <div>{item.officialSite}</div>}
      </>
    );
  };

  return (
    <Section<Education>
      section={section}
      summaryKey="summary"
      leftRender={leftRender}
      rightRender={rightRender}
    ></Section>
  );
};

const Awards = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.awards;
  const leftRender = (item: Award) => {
    return (
      <>
        <div>{item.date}</div>
      </>
    );
  };

  const rightRender = (item: Award) => {
    return (
      <div className="flex justify-between text-base font-bold">
        <div className="text-base font-bold">{item.title}</div>
        <div>{item.awarder}</div>
      </div>
    );
  };

  return (
    <Section<Award>
      section={section}
      urlKey="url"
      summaryKey="summary"
      leftRender={leftRender}
      rightRender={rightRender}
    ></Section>
  );
};

const Certifications = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.certifications;

  const leftRender = (item: Certification) => {
    return (
      <>
        <div>{item.date}</div>
      </>
    );
  };

  const rightRender = (item: Certification) => {
    return (
      <div className="flex justify-between text-base font-bold">
        <div>{item.name}</div>
        <div>{item.issuer}</div>
      </div>
    );
  };

  return (
    <Section<Certification>
      section={section}
      urlKey="url"
      summaryKey="summary"
      leftRender={leftRender}
      rightRender={rightRender}
    ></Section>
  );
};

const Skills = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.skills;
  // const leftRender = (item: Skill) => {
  //   return (
  //     <>
  //       <div className="text-base font-bold">{item.name}</div>
  //     </>
  //   );
  // };

  const rightRender = (item: Skill) => {
    return (
      <>
        <div className="flex items-center justify-between">
          <div className="text-base font-bold">{item.description}</div>
          <div className="text-base font-bold">{item.name}</div>
        </div>
      </>
    );
  };

  return (
    <Section<Skill>
      section={section}
      levelKey="level"
      keywordsKey="keywords"
      rightRender={rightRender}
    ></Section>
  );
};

const Interests = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.interests;

  const rightRender = (item: Interest) => {
    return (
      <>
        <div className="text-base font-bold">{item.name}</div>
      </>
    );
  };

  return (
    <Section<Interest>
      section={section}
      keywordsKey="keywords"
      className="space-y-0.5"
      rightRender={rightRender}
    ></Section>
  );
};

const Publications = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.publications;
  const leftRender = (item: Publication) => {
    return (
      <>
        <div>{item.date}</div>
      </>
    );
  };

  const rightRender = (item: Publication) => {
    return (
      <div className="flex justify-between text-base font-bold">
        <div>{item.name}</div>
        <div>{item.publisher}</div>
      </div>
    );
  };
  return (
    <Section<Publication>
      section={section}
      urlKey="url"
      summaryKey="summary"
      leftRender={leftRender}
      rightRender={rightRender}
    ></Section>
  );
};

const Volunteer = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.volunteer;

  const leftRender = (item: Volunteer) => {
    return (
      <>
        <div>{item.date}</div>
      </>
    );
  };

  const rightRender = (item: Volunteer) => {
    return (
      <>
        <div className="flex justify-between text-base font-bold">
          <div>{item.organization}</div>
          <div>{item.location}</div>
        </div>
        <div>{item.position}</div>
      </>
    );
  };

  return (
    <Section<Volunteer>
      section={section}
      urlKey="url"
      summaryKey="summary"
      leftRender={leftRender}
      rightRender={rightRender}
    ></Section>
  );
};

const Languages = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.languages;

  const rightRender = (item: Language) => {
    return (
      <>
        <div className="text-base font-bold">{item.name}</div>
        <div>{item.description}</div>
      </>
    );
  };

  return (
    <Section<Language>
      section={section}
      levelKey="level"
      rightRender={rightRender}
    ></Section>
  );
};

const Projects = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.projects;

  const leftRender = (item: Project) => {
    return (
      <>
        <div>{item.date}</div>
      </>
    );
  };

  const rightRender = (item: Project) => {
    return (
      <div className="flex justify-between text-base font-bold">
        <div>{item.name}</div>
        <div>{item.description}</div>
      </div>
    );
  };
  return (
    <Section<Project>
      section={section}
      urlKey="url"
      summaryKey="summary"
      keywordsKey="keywords"
      leftRender={leftRender}
      rightRender={rightRender}
    ></Section>
  );
};

const References = ({ resume }: { resume: TResume }) => {
  const section = resume.data.sections?.references;

  const rightRender = (item: Reference) => {
    return (
      <>
        <div className="text-base font-bold">{item.name}</div>
        <div>{item.description}</div>
      </>
    );
  };
  return (
    <Section<Reference>
      section={section}
      urlKey="url"
      summaryKey="summary"
      rightRender={rightRender}
    ></Section>
  );
};

const Custom = ({ id, resume }: { id: string; resume: TResume }) => {
  const section = resume.data.sections?.custom?.[id];

  const leftRender = (item: CustomSection) => {
    return (
      <>
        <div>{item.date}</div>
      </>
    );
  };

  const rightRender = (item: CustomSection) => {
    if (!item.name && !item.location && !item.description) {
      return null;
    }
    return (
      <>
        <div className="flex justify-between text-base font-bold">
          <div>{item.name}</div>
          <div>{item.location}</div>
        </div>
        <div>{item.description}</div>
      </>
    );
  };
  return (
    <Section<CustomSection>
      section={section}
      urlKey="url"
      summaryKey="summary"
      keywordsKey="keywords"
      leftRender={leftRender}
      rightRender={rightRender}
    ></Section>
  );
};

const mapSectionToComponent = (section: any, resume: TResume) => {
  switch (section) {
    case "profiles":
      return <Profiles resume={resume} />;
    case "summary":
      return <Summary resume={resume} />;
    case "workExperience":
      return <Experience resume={resume} />;
    case "internship":
      return <Internship resume={resume} />;
    case "education":
      return <Education resume={resume} />;
    case "awards":
      return <Awards resume={resume} />;
    case "certifications":
      return <Certifications resume={resume} />;
    case "skills":
      return <Skills resume={resume} />;
    case "interests":
      return <Interests resume={resume} />;
    case "publications":
      return <Publications resume={resume} />;
    case "volunteer":
      return <Volunteer resume={resume} />;
    case "languages":
      return <Languages resume={resume} />;
    case "projects":
      return <Projects resume={resume} />;
    case "references":
      return <References resume={resume} />;
    default:
      if (section.startsWith("custom."))
        return <Custom id={section.split(".")[1]} resume={resume} />;

      return null;
  }
};

export const Simple002 = ({
  resume,
  columns,
  isFirstPage = false,
}: TemplateProps) => {
  const [main, sidebar] = columns;

  return (
    <div className="space-y-5 p-custom">
      {isFirstPage && <Header resume={resume} />}

      {main.map((section) => (
        <Fragment key={section}>
          {mapSectionToComponent(section, resume)}
        </Fragment>
      ))}

      {sidebar.map((section) => (
        <Fragment key={section}>
          {mapSectionToComponent(section, resume)}
        </Fragment>
      ))}
    </div>
  );
};
